import type { IssueGrouping } from "./groupAndSort";
import { unitsOf } from "./issuePlacement";

/**
 * The Gantt's rows as geometry, and what a vertical drag does to them.
 *
 * Rows are positioned by arithmetic rather than measured, so the inverse — a
 * dragged block's y back to a place in a band — is arithmetic too. That keeps
 * it pure and testable, and correct while the body is scrolled, because the
 * caller hands in body-relative coordinates.
 *
 * What moves is a BLOCK: a top-level row carries the subissues glued beneath
 * it, exactly as the arrangement does (issuePlacement.unitsOf). A subissue
 * moves on its own, but only among its siblings — its band and its place in
 * the chart come from its parent.
 */

export interface GanttBandSlot {
  key: string;
  /** Distance from the top of the body to the top of the band's header. */
  top: number;
  collapsed: boolean;
}

export interface GanttRowSlot {
  groupKey: string;
  issueId: number;
  indentLevel: number;
  /** The top-level issue of the block this row belongs to. */
  unitId: number;
  top: number;
}

export interface GanttLayout {
  bands: GanttBandSlot[];
  slots: GanttRowSlot[];
  height: number;
}

/**
 * Walk the groups exactly as the renderer does. An issue that appears in
 * several bands (multi-value grouping) gets a slot in each — the pointer is
 * over one specific row, not over "the issue".
 */
export function buildGanttLayout(
  groups: readonly IssueGrouping[],
  collapsed: ReadonlySet<string>,
  groupRowHeight: number,
  rowHeight: number
): GanttLayout {
  const bands: GanttBandSlot[] = [];
  const slots: GanttRowSlot[] = [];
  let y = 0;
  for (const group of groups) {
    const isCollapsed = collapsed.has(group.key);
    bands.push({ key: group.key, top: y, collapsed: isCollapsed });
    y += groupRowHeight;
    if (isCollapsed) continue;
    let unitId = -1;
    for (const item of group.items) {
      if (item.indentLevel === 0 || unitId === -1) unitId = item.issue.id;
      slots.push({
        groupKey: group.key,
        issueId: item.issue.id,
        indentLevel: item.indentLevel,
        unitId,
        top: y,
      });
      y += rowHeight;
    }
  }
  return { bands, slots, height: y };
}

/** The first row of each issue, which is where its arrows attach. */
export function firstRowTops(layout: GanttLayout): Map<number, number> {
  const tops = new Map<number, number>();
  for (const slot of layout.slots) {
    if (!tops.has(slot.issueId)) tops.set(slot.issueId, slot.top);
  }
  return tops;
}

/** What a vertical drag picked up. */
export interface RowDragSource {
  groupKey: string;
  issueId: number;
  unitId: number;
  /** A subissue, which reorders among its siblings only. */
  child: boolean;
}

export function dragSourceAt(
  layout: GanttLayout,
  groupKey: string,
  issueId: number
): RowDragSource | null {
  const slot = layout.slots.find((s) => s.groupKey === groupKey && s.issueId === issueId);
  if (!slot) return null;
  return { groupKey, issueId, unitId: slot.unitId, child: slot.indentLevel > 0 };
}

/** The rows that travel with the pointer, in order. */
export function draggedSlots(layout: GanttLayout, source: RowDragSource, groupKey: string) {
  return layout.slots.filter((slot) =>
    source.child
      ? slot.groupKey === groupKey && slot.issueId === source.issueId
      : slot.groupKey === groupKey && slot.unitId === source.unitId
  );
}

/** Where the block would land: a band, and an index among the blocks (or
 *  siblings) already in it, not counting the dragged one. */
export interface RowDropTarget {
  groupKey: string;
  index: number;
}

/**
 * Resolve the dragged block's current top to a drop target.
 *
 * Measured against the layout as it was when the drag began, not the live
 * preview: the preview moves rows out of the way, and resolving against it
 * would move them back, so the target would flicker between two answers.
 *
 * The block's CENTRE decides, the way it does on the board: a block lands
 * below whatever block's middle it has passed.
 *
 * @param canEnter whether a band other than the source may take the block —
 *   only bands whose key is a value an issue can be given. Returning null for
 *   the rest is how the refusal is shown: nothing makes room.
 */
export function rowDropTarget(
  layout: GanttLayout,
  source: RowDragSource,
  blockTop: number,
  rowHeight: number,
  canEnter: (groupKey: string) => boolean
): RowDropTarget | null {
  const blockRows = draggedSlots(layout, source, source.groupKey).length || 1;
  const centre = blockTop + (blockRows * rowHeight) / 2;

  if (source.child) {
    const siblings = layout.slots.filter(
      (slot) =>
        slot.groupKey === source.groupKey &&
        slot.unitId === source.unitId &&
        slot.indentLevel > 0 &&
        slot.issueId !== source.issueId
    );
    const index = siblings.filter((slot) => slot.top + rowHeight / 2 < centre).length;
    return { groupKey: source.groupKey, index };
  }

  if (layout.bands.length === 0) return null;
  let band = layout.bands[0];
  for (const candidate of layout.bands) {
    if (candidate.top <= centre) band = candidate;
  }
  if (band.key !== source.groupKey && !canEnter(band.key)) return null;

  // Each other block's middle, in band order.
  const middles: number[] = [];
  let blockStart: number | null = null;
  let blockEnd = 0;
  let currentUnit: number | null = null;
  for (const slot of layout.slots) {
    if (slot.groupKey !== band.key || slot.unitId === source.unitId) continue;
    if (slot.unitId !== currentUnit) {
      if (blockStart !== null) middles.push((blockStart + blockEnd) / 2);
      currentUnit = slot.unitId;
      blockStart = slot.top;
    }
    blockEnd = slot.top + rowHeight;
  }
  if (blockStart !== null) middles.push((blockStart + blockEnd) / 2);

  return { groupKey: band.key, index: middles.filter((middle) => middle < centre).length };
}

/**
 * The groups as they would read with the block dropped at `target` — what the
 * chart shows while the drag is live, so rows make room under the pointer.
 *
 * A block entering a band it already has a copy in (an issue with two
 * assignees, dragged into the other's band) replaces that copy rather than
 * appearing twice.
 */
export function previewDrop(
  groups: readonly IssueGrouping[],
  source: RowDragSource,
  target: RowDropTarget
): IssueGrouping[] {
  if (source.child) {
    return groups.map((group) => {
      if (group.key !== source.groupKey) return group;
      const units = unitsOf(group.items).map((unit) => {
        if (unit.head.issue.id !== source.unitId) return unit;
        const moved = unit.children.find((child) => child.issue.id === source.issueId);
        if (!moved) return unit;
        const rest = unit.children.filter((child) => child.issue.id !== source.issueId);
        const at = Math.max(0, Math.min(target.index, rest.length));
        return { ...unit, children: [...rest.slice(0, at), moved, ...rest.slice(at)] };
      });
      return { ...group, items: units.flatMap((unit) => [unit.head, ...unit.children]) };
    });
  }

  // Looked for in the target band too: after a drop into another status the
  // store moves the issue there before the arrangement is saved, and the
  // preview has to keep holding it in place across that gap.
  const findBlock = (groupKey: string) => {
    const group = groups.find((g) => g.key === groupKey);
    return group
      ? unitsOf(group.items).find((unit) => unit.head.issue.id === source.unitId)
      : undefined;
  };
  const block = findBlock(source.groupKey) ?? findBlock(target.groupKey);
  if (!block) return [...groups];

  return groups.map((group) => {
    if (group.key !== source.groupKey && group.key !== target.groupKey) return group;
    const units = unitsOf(group.items).filter((unit) => unit.head.issue.id !== source.unitId);
    if (group.key === target.groupKey) {
      const at = Math.max(0, Math.min(target.index, units.length));
      units.splice(at, 0, block);
    }
    return { ...group, items: units.flatMap((unit) => [unit.head, ...unit.children]) };
  });
}

/**
 * The order a band stores after a drop: its blocks' top-level ids, or — for a
 * subissue — its siblings' ids. The same ids `arrangeGroups` reads back.
 */
export function bandOrder(
  groups: readonly IssueGrouping[],
  source: RowDragSource,
  groupKey: string
): number[] {
  const group = groups.find((g) => g.key === groupKey);
  if (!group) return [];
  const units = unitsOf(group.items);
  if (!source.child) return units.map((unit) => unit.head.issue.id);
  const parent = units.find((unit) => unit.head.issue.id === source.unitId);
  return (parent?.children ?? []).map((child) => child.issue.id);
}
